import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';

import type { Dispatch } from 'react';
import type {
  Annotation,
  EliceHtmlViewerProps,
  HtmlViewerAction,
  HtmlViewerState,
  ViewerMode,
} from './HtmlViewerTypes';

// ─── Context ─────────────────────────────────────────────────────────────────

interface HtmlViewerContextType {
  state: HtmlViewerState;
  dispatch: Dispatch<HtmlViewerAction>;
}

const HtmlViewerContext = createContext<HtmlViewerContextType | null>(null);

// ─── Reducer ─────────────────────────────────────────────────────────────────

const initialState: HtmlViewerState = {
  mode: 'read',
  annotations: [],
  activeAnnotationId: null,
  pendingRange: null,
  menuPosition: null,
};

function htmlViewerReducer(
  state: HtmlViewerState,
  action: HtmlViewerAction
): HtmlViewerState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload };

    case 'ADD_ANNOTATION':
      return { ...state, annotations: [...state.annotations, action.payload] };

    case 'UPDATE_ANNOTATION':
      return {
        ...state,
        annotations: state.annotations.map(a =>
          a.id === action.payload.id ? action.payload : a
        ),
      };

    case 'DELETE_ANNOTATION':
      return {
        ...state,
        annotations: state.annotations.filter(a => a.id !== action.payload),
        activeAnnotationId:
          state.activeAnnotationId === action.payload
            ? null
            : state.activeAnnotationId,
        menuPosition:
          state.activeAnnotationId === action.payload
            ? null
            : state.menuPosition,
      };

    case 'SET_ACTIVE_ANNOTATION':
      return { ...state, activeAnnotationId: action.payload };

    case 'SET_PENDING_RANGE':
      return { ...state, pendingRange: action.payload };

    case 'SET_MENU_POSITION':
      return { ...state, menuPosition: action.payload };

    case 'SYNC_ANNOTATIONS':
      return { ...state, annotations: action.payload };

    default:
      return state;
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

type ProviderProps = Pick<
  EliceHtmlViewerProps,
  | 'defaultMode'
  | 'annotations'
  | 'onAnnotationCreate'
  | 'onAnnotationUpdate'
  | 'onAnnotationDelete'
> & {
  children: React.ReactNode;
};

export const HtmlViewerProvider = ({
  children,
  defaultMode = 'read',
  annotations: controlledAnnotations,
  onAnnotationCreate,
  onAnnotationUpdate,
  onAnnotationDelete,
}: ProviderProps) => {
  const [state, dispatch] = useReducer(htmlViewerReducer, {
    ...initialState,
    mode: defaultMode as ViewerMode,
  });

  // Keep latest callbacks in refs to prevent stale closures
  const onCreateRef = useRef(onAnnotationCreate);
  const onUpdateRef = useRef(onAnnotationUpdate);
  const onDeleteRef = useRef(onAnnotationDelete);

  useEffect(() => {
    onCreateRef.current = onAnnotationCreate;
  }, [onAnnotationCreate]);
  useEffect(() => {
    onUpdateRef.current = onAnnotationUpdate;
  }, [onAnnotationUpdate]);
  useEffect(() => {
    onDeleteRef.current = onAnnotationDelete;
  }, [onAnnotationDelete]);

  // Sync controlled annotations
  useEffect(() => {
    if (controlledAnnotations !== undefined) {
      dispatch({ type: 'SYNC_ANNOTATIONS', payload: controlledAnnotations });
    }
  }, [controlledAnnotations]);

  // Fire external callbacks when internal state changes
  const prevAnnotationsRef = useRef<Annotation[]>(state.annotations);
  useEffect(() => {
    const prev = prevAnnotationsRef.current;
    const curr = state.annotations;

    if (prev === curr) return;

    // Detect added annotation
    if (curr.length > prev.length) {
      const added = curr.find(a => !prev.some(p => p.id === a.id));
      if (added) onCreateRef.current?.(added);
    }
    // Detect updated annotation
    else if (curr.length === prev.length) {
      const updated = curr.find(a => {
        const prevA = prev.find(p => p.id === a.id);
        return prevA && (prevA.memo !== a.memo || prevA.color !== a.color);
      });
      if (updated) onUpdateRef.current?.(updated);
    }
    // Detect deleted annotation
    else {
      const deleted = prev.find(p => !curr.some(a => a.id === p.id));
      if (deleted) onDeleteRef.current?.(deleted.id);
    }

    prevAnnotationsRef.current = curr;
  }, [state.annotations]);

  return (
    <HtmlViewerContext.Provider value={{ state, dispatch }}>
      {children}
    </HtmlViewerContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useHtmlViewer = (): HtmlViewerContextType => {
  const context = useContext(HtmlViewerContext);
  if (!context) {
    throw new Error('useHtmlViewer must be used within HtmlViewerProvider');
  }
  return context;
};
